/**
 * 实交档位(1005 生产验收:3:4 选 2K 收 66 积分,下载原件 1086×1448)。
 *
 * 请求尺寸是对的,GPT 线路的中转上游有时不认 size,按 ~1.57MP 自选画布交图。页面上
 * 「2K」这个字只能说**请求**了几 K;真交了几 K 要看像素。规则与 ai
 * `_tier_ratio_tables.delivered_tier` 逐字同一条(改一处要同步改另一处):
 *
 *   实交档 = 满足「实交像素 ≥ 该档该比例标称像素 × 90%」的最高档;都不满足记 1K。
 *   标称像素 = OPENAI 表(全部引擎里同档最小的那张)。
 */
export type DeliveredTier = "1K" | "2K" | "4K";

const NOMINAL: Record<string, Record<DeliveredTier, [number, number]>> = {
  "1:1": { "1K": [1024, 1024], "2K": [2048, 2048], "4K": [2880, 2880] },
  "9:16": { "1K": [864, 1536], "2K": [1152, 2048], "4K": [2160, 3840] },
  "16:9": { "1K": [1536, 864], "2K": [2048, 1152], "4K": [3840, 2160] },
  "3:4": { "1K": [1056, 1408], "2K": [1536, 2048], "4K": [2448, 3264] },
  "4:5": { "1K": [1024, 1280], "2K": [1664, 2080], "4K": [2560, 3200] },
};
/** 没给比例(套图逐张比例不一)时按实交宽高比挑最近的标称比例。 */
function nearestRatio(width: number, height: number): string {
  if (!width || !height) return "1:1";
  const ar = width / height;
  let best = "1:1";
  let diff = Infinity;
  for (const [key, row] of Object.entries(NOMINAL)) {
    const [w, h] = row["1K"];
    const d = Math.abs(w / h - ar);
    if (d < diff) {
      diff = d;
      best = key;
    }
  }
  return best;
}

const ORDER: DeliveredTier[] = ["1K", "2K", "4K"];
const TOLERANCE = 0.9;

export function deliveredTier(
  ratio: string | null | undefined,
  width: number,
  height: number
): DeliveredTier {
  const table = NOMINAL[ratio ?? ""] ?? NOMINAL[nearestRatio(width, height)];
  const actual = Math.max(width, 0) * Math.max(height, 0);
  let best: DeliveredTier = "1K";
  for (const tier of ORDER) {
    const [w, h] = table[tier];
    if (actual >= w * h * TOLERANCE) best = tier;
  }
  return best;
}

function rank(tier: string | null | undefined): number {
  const i = ORDER.indexOf(String(tier ?? "1K").toUpperCase() as DeliveredTier);
  return i < 0 ? 0 : i;
}

/**
 * 实交低于请求档时给一句对客说明,否则 null。
 * 例:「实际交付 1086×1448(1K 规格),低于所选 2K」
 */
export function shortfallNote(
  requestedTier: string | null | undefined,
  ratio: string | null | undefined,
  width: number | null | undefined,
  height: number | null | undefined
): string | null {
  if (!requestedTier || !width || !height) return null;
  const got = deliveredTier(ratio, width, height);
  if (rank(got) >= rank(requestedTier)) return null;
  return `实际交付 ${width}×${height}(${got} 规格),低于所选 ${requestedTier}`;
}
